import React, { useState, useEffect, useCallback } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  NavLink,
  Link,
  Navigate,
  useNavigate,
  useParams,
  useLocation
} from 'react-router-dom';
import axios from 'axios';
import './App.css';
import {
  featureCards,
  heroBullets,
  screenshots,
  videos,
  helpTopics,
  staticPages
} from './content';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

/* ------------------------------------------------------------------ */
/*  Shell: header, sidebar, footer                                     */
/* ------------------------------------------------------------------ */

const PUBLIC_NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/help', label: 'Help', end: false },
  { to: '/about', label: 'About', end: true }
];

function Header({
  user,
  sidebarOpen,
  onToggleSidebar,
  mobileNavOpen,
  onToggleMobileNav,
  onLogout
}) {
  return (
    <header className="header">
      <div className="header-content">
        {user ? (
          <button
            className="collapse-btn"
            onClick={onToggleSidebar}
            aria-label={sidebarOpen ? 'Hide navigation' : 'Show navigation'}
            aria-expanded={sidebarOpen}
          >
            <span className="icon-desktop">{sidebarOpen ? '\u25C0' : '\u25B6'}</span>
            <span className="icon-mobile">{'\u2630'}</span>
          </button>
        ) : (
          <button
            className="collapse-btn mobile-only"
            onClick={onToggleMobileNav}
            aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileNavOpen}
          >
            {'\u2630'}
          </button>
        )}

        <Link to={user ? '/home' : '/'} className="logo-section">
          <img src="/lusaas_mark_80.png" alt="" className="logo-icon" />
          <span className="brand">LuSaaS Trader</span>
        </Link>

        {!user && (
          <nav className="top-nav">
            {PUBLIC_NAV.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) => `top-nav-link ${isActive ? 'active' : ''}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        )}

        <div className="user-info">
          {user ? (
            <>
              <span className="user-name">{user.name || user.email}</span>
              <button className="btn-logout" onClick={onLogout}>
                Logout
              </button>
            </>
          ) : (
            <>
              <Link className="btn btn-secondary btn-sm" to="/login">
                Sign in
              </Link>
              <Link className="btn btn-primary btn-sm" to="/register">
                Sign up
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

// Slide-down menu for visitors on small screens.
function MobileNav({ open, onClose }) {
  if (!open) return null;
  return (
    <nav className="mobile-nav">
      {PUBLIC_NAV.map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          end={l.end}
          className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
          onClick={onClose}
        >
          {l.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Sidebar({ isAdmin, open, onNavigate }) {
  const links = [
    { to: '/home', label: 'Home' },
    { to: '/products', label: 'Downloads' },
    { to: '/profile', label: 'Profile' },
    ...(isAdmin
      ? [
          { to: '/admin', label: 'Manage products' },
          { to: '/admin/licenses', label: 'Licences' },
          { to: '/admin/messages', label: 'Messages' }
        ]
      : []),
    { to: '/help', label: 'Help' }
  ];

  return (
    <aside className={`sidebar ${open ? 'open' : 'closed'}`}>
      <nav className="sidebar-nav">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === '/help'}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            onClick={onNavigate}
          >
            {l.label}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-column">
          <h4>About</h4>
          <ul>
            <li>
              <Link to="/about">About LuSaaS</Link>
            </li>
          </ul>
        </div>
        <div className="footer-column">
          <h4>Support</h4>
          <ul>
            <li>
              <Link to="/help">Help Center</Link>
            </li>
            <li>
              <Link to="/help/how-to-use">Documentation</Link>
            </li>
            <li>
              <Link to="/help/contact">Contact Us</Link>
            </li>
          </ul>
        </div>
        <div className="footer-column">
          <h4>Legal</h4>
          <ul>
            <li>
              <Link to="/privacy">Privacy Policy</Link>
            </li>
            <li>
              <Link to="/terms">Terms of Service</Link>
            </li>
            <li>
              <Link to="/disclaimer">Legal Disclaimer</Link>
            </li>
          </ul>
        </div>
        <div className="footer-column">
          <h4>Follow Us</h4>
          <p className="social-note">Accounts coming soon.</p>
        </div>
      </div>
      <div className="footer-bottom">
        <p>&copy; 2026 LuSaaS Trader. All rights reserved.</p>
        <p className="disclaimer-footer">
          Past performance is not indicative of future results. Trading futures,
          options and forex involves substantial risk of loss and is not suitable
          for every investor.
        </p>
      </div>
    </footer>
  );
}

/* ------------------------------------------------------------------ */
/*  Reusable pieces                                                    */
/* ------------------------------------------------------------------ */

function PageHeader({ title, description }) {
  return (
    <div className="section-header">
      <h1>{title}</h1>
      {description && <p>{description}</p>}
    </div>
  );
}

function Message({ text, tone = 'error' }) {
  if (!text) return null;
  return <div className={`message message-${tone}`}>{text}</div>;
}

function ReadOnlyField({ label, value }) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <span className={`field-value ${value ? '' : 'field-empty'}`}>
        {value || 'Not set'}
      </span>
    </div>
  );
}

// Loads the YouTube player only after a click, so the page does not pull
// several hundred KB of player script on every visit.
function VideoEmbed({ id, title }) {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <div className="video-frame">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      className="video-facade"
      onClick={() => setPlaying(true)}
      aria-label={`Play ${title}`}
    >
      <img
        src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
        alt=""
        loading="lazy"
      />
      <span className="video-play" aria-hidden="true" />
      <span className="video-title">{title}</span>
    </button>
  );
}

/**
 * Screenshots with click-to-enlarge. The captures are 1920px wide and
 * unreadable at card size, so the lightbox is not decoration here.
 */
function ScreenshotGrid({ items, pageLayout = false }) {
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (open === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(null);
      if (e.key === 'ArrowRight') setOpen((i) => (i + 1) % items.length);
      if (e.key === 'ArrowLeft') setOpen((i) => (i - 1 + items.length) % items.length);
    };
    document.addEventListener('keydown', onKey);
    // stop the page scrolling behind the overlay
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, items.length]);

  return (
    <>
      <div className={`shots-grid ${pageLayout ? 'shots-grid-page' : ''}`}>
        {items.map((s, i) => (
          <figure className="shot" key={s.src}>
            <button
              className="shot-btn"
              onClick={() => setOpen(i)}
              aria-label={`Enlarge: ${s.caption}`}
            >
              <img src={s.src} alt={s.caption} loading="lazy" />
              <span className="shot-zoom" aria-hidden="true">
                {'\u2922'}
              </span>
            </button>
            <figcaption>{s.caption}</figcaption>
          </figure>
        ))}
      </div>

      {open !== null && (
        <div
          className="lightbox"
          onClick={() => setOpen(null)}
          role="dialog"
          aria-modal="true"
          aria-label={items[open].caption}
        >
          <button className="lightbox-close" onClick={() => setOpen(null)} aria-label="Close">
            {'\u00D7'}
          </button>

          {items.length > 1 && (
            <button
              className="lightbox-nav lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((i) => (i - 1 + items.length) % items.length);
              }}
              aria-label="Previous"
            >
              {'\u2039'}
            </button>
          )}

          <figure className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <img src={items[open].src} alt={items[open].caption} />
            <figcaption>
              {items[open].caption}
              {items.length > 1 && (
                <span className="lightbox-count">
                  {open + 1} of {items.length}
                </span>
              )}
            </figcaption>
          </figure>

          {items.length > 1 && (
            <button
              className="lightbox-nav lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((i) => (i + 1) % items.length);
              }}
              aria-label="Next"
            >
              {'\u203A'}
            </button>
          )}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Public pages                                                       */
/* ------------------------------------------------------------------ */

function Landing() {
  return (
    <div className="landing-page">
      <section className="hero-section">
        <p className="hero-badge">Interactive Brokers accounts only</p>
        <h1>One cockpit. Multiple strategies.</h1>
        <ul className="hero-bullets">
          {heroBullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      </section>

      {videos.length > 0 && (
        <section className="videos-section">
          <h2>See it in action</h2>
          <div className="videos-grid">
            {videos.map((v) => (
              <VideoEmbed key={v.id} id={v.id} title={v.title} />
            ))}
          </div>
        </section>
      )}

      <section className="features-section">
        <h2>Institutional Risk Protocols</h2>
        <div className="features-grid">
          {featureCards.map((card, i) => (
            <article className="feature-card" key={i}>
              <h3>{card.title}</h3>
              <ul className="card-bullets">
                {card.bullets.map((b, j) => (
                  <li key={j}>{b}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
        <p className="swipe-hint">Swipe to see more</p>
      </section>

      {screenshots.length > 0 && (
        <section className="shots-section">
          <h2>Inside the platform</h2>
          <ScreenshotGrid items={screenshots} />
          <p className="swipe-hint">Swipe to see more \u00B7 tap to enlarge</p>
        </section>
      )}

      <section className="cta-section">
        <h2>Start your free 90-day LuSaaS Trader evaluation today</h2>
        <p>Works with your Interactive Brokers account.</p>
        <div className="cta-buttons">
          <Link className="btn btn-primary" to="/register">
            Create account
          </Link>
          <Link className="btn btn-secondary" to="/login">
            Sign in
          </Link>
        </div>
      </section>
    </div>
  );
}

function StaticPage({ slug }) {
  const params = useParams();
  const key = slug || params.slug;
  const page = staticPages[key];

  if (!page) return <NotFound />;

  // `body` is either a single string, or a list whose entries are strings
  // (plain paragraphs) or { heading, text } objects (a titled section).
  const blocks = Array.isArray(page.body) ? page.body : [page.body];

  return (
    <div className="prose-page">
      <PageHeader title={page.title} />
      <div className="prose">
        {blocks.map((b, i) =>
          typeof b === 'string' ? (
            <p key={i}>{b}</p>
          ) : (
            <section key={i} className="prose-section">
              <h2>{b.heading}</h2>
              <p>{b.text}</p>
            </section>
          )
        )}
      </div>
    </div>
  );
}

function HelpIndex() {
  return (
    <div className="dashboard">
      <PageHeader
        title="Help and documentation"
        description="Guides, tutorials and support for LuSaaS Trader."
      />
      <div className="help-grid">
        {helpTopics.map((t) => (
          <article className="help-card" key={t.slug}>
            <h3>{t.title}</h3>
            <p>{t.summary}</p>
            <Link className="btn btn-primary" to={`/help/${t.slug}`}>
              Open
            </Link>
          </article>
        ))}
      </div>
    </div>
  );
}

function HelpTopic({ contactUser }) {
  const { slug } = useParams();

  if (slug === 'screenshots') {
    return (
      <div className="dashboard">
        <PageHeader title="Screenshots" description="Walkthroughs of the main screens." />
        {screenshots.length === 0 ? (
          <div className="empty-state">
            <p>Screenshots are being prepared and will appear here shortly.</p>
          </div>
        ) : (
          <ScreenshotGrid items={screenshots} pageLayout />
        )}
      </div>
    );
  }

  if (slug === 'contact') {
    return <ContactForm user={contactUser} />;
  }

  if (slug === 'videos') {
    return (
      <div className="dashboard">
        <PageHeader title="Video tutorials" description="Setup and daily use, step by step." />
        {videos.length === 0 ? (
          <div className="empty-state">
            <p>Videos are being recorded and will appear here shortly.</p>
          </div>
        ) : (
          <div className="videos-grid">
            {videos.map((v) => (
              <VideoEmbed key={v.id} id={v.id} title={v.title} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return <StaticPage />;
}

function ContactForm({ user }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: ''
  });
  const [msg, setMsg] = useState({ text: '', tone: 'error' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, name: user.name || '', email: user.email || '' }));
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ text: '', tone: 'error' });
    setSending(true);
    try {
      const headers = localStorage.getItem('token') ? authHeader() : {};
      const { data } = await axios.post(`${API_URL}/contact`, form, headers);
      setSent(true);
      setMsg({ text: data.message, tone: 'success' });
    } catch (err) {
      setMsg({
        text: err.response?.data?.error || 'Could not send your message. Please try again.',
        tone: 'error'
      });
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="dashboard">
        <PageHeader title="Contact support" />
        <div className="panel">
          <Message text={msg.text} tone="success" />
          <p className="muted">
            We read everything that comes through here and will reply to{' '}
            {form.email}. If it is urgent, send it again in a day or two rather
            than assuming it was missed.
          </p>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setSent(false);
              setForm((f) => ({ ...f, subject: '', message: '' }));
              setMsg({ text: '', tone: 'error' });
            }}
          >
            Send another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <PageHeader
        title="Contact support"
        description="Questions about the software, your licence, or anything else."
      />

      <div className="contact-layout">
        <div className="panel">
          <Message text={msg.text} tone={msg.tone} />
          <form onSubmit={submit}>
            <div className="form-group">
              <label htmlFor="c-name">Your name</label>
              <input
                id="c-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="form-group">
              <label htmlFor="c-email">Email</label>
              <input
                id="c-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Where we should reply"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="c-subject">Subject</label>
              <input
                id="c-subject"
                type="text"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Licence question, bug report, something else"
              />
            </div>
            <div className="form-group">
              <label htmlFor="c-message">Message</label>
              <textarea
                id="c-message"
                rows="8"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us what is going on"
                required
              />
            </div>

            {/* Honeypot. Hidden from people, irresistible to bots. */}
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="c-website">Website</label>
              <input
                id="c-website"
                type="text"
                tabIndex="-1"
                autoComplete="off"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>

            <button type="submit" className="btn btn-primary full-width" disabled={sending}>
              {sending ? 'Sending...' : 'Send message'}
            </button>
          </form>
        </div>

        <aside className="panel contact-aside">
          <h2>Before you write</h2>
          <p className="muted">
            If the engine will not start, the message printed in the console
            usually says why. Including it here saves a round trip.
          </p>
          <p className="muted">
            Licence bound to the wrong machine? You can move it yourself from
            your <Link to="/profile">profile page</Link> without waiting for us.
          </p>
          <p className="muted">
            Setting things up for the first time? The{' '}
            <Link to="/help/how-to-use">how to use</Link> and{' '}
            <Link to="/help/install-tws">TWS connection</Link> pages cover most
            of it.
          </p>
          <p className="muted">
            Prefer email? Write to{' '}
            <a href="mailto:contact@lusaas.com">contact@lusaas.com</a> instead.
            The form just saves you opening a mail client.
          </p>
        </aside>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="prose-page">
      <PageHeader
        title="Page not found"
        description="That page does not exist or has moved."
      />
      <Link className="btn btn-primary" to="/">
        Back to home
      </Link>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Auth pages                                                         */
/* ------------------------------------------------------------------ */

function Register({ onAuth }) {
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/register`, form);
      onAuth(data);
      navigate('/home');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-section">
      <div className="auth-card">
        <h1>Create account</h1>
        <Message text={error} />
        <form onSubmit={submit}>
          <div className="form-group">
            <label htmlFor="reg-name">Full name</label>
            <input
              id="reg-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
            />
          </div>
          <div className="form-group">
            <label htmlFor="reg-email">Email</label>
            <input
              id="reg-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="reg-password">Password</label>
            <input
              id="reg-password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="At least 6 characters"
              required
            />
          </div>
          <button type="submit" className="btn btn-primary full-width" disabled={loading}>
            {loading ? 'Creating account...' : 'Create account'}
          </button>
        </form>
        <p className="auth-link">
          Already registered? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

function Login({ onAuth }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/login`, form);
      onAuth(data);
      navigate('/home');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-section">
      <div className="auth-card">
        <h1>Sign in</h1>
        <Message text={error} />
        <form onSubmit={submit}>
          <div className="form-group">
            <label htmlFor="log-email">Email</label>
            <input
              id="log-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="log-password">Password</label>
            <input
              id="log-password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Your password"
              required
            />
          </div>
          <button type="submit" className="btn btn-primary full-width" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
        <p className="auth-link">
          No account yet? <Link to="/register">Create one</Link>
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Signed-in pages                                                    */
/* ------------------------------------------------------------------ */

function Home({ user, profile, products }) {
  const licenses = profile?.licenses || [];
  const latest = products[0];

  return (
    <div className="dashboard">
      <PageHeader
        title={`Welcome back, ${user.name || user.email}`}
        description="Your licence status and latest release."
      />

      <div className="home-grid">
        <section className="panel">
          <h2>Licence</h2>
          {licenses.length === 0 ? (
            <p className="muted">
              No licence is linked to this email yet. Contact support if you
              believe this is an error.
            </p>
          ) : (
            licenses.map((l, i) => (
              <div className="licence-summary" key={l.id}>
                <h3>
                  {l.product || 'IBKR'}
                  {licenses.length > 1 ? ` \u00B7 seat ${i + 1}` : ''}
                </h3>
                <ReadOnlyField label="Key" value={l.license_key} />
                <ReadOnlyField label="Type" value={l.license_type} />
                <ReadOnlyField label="Status" value={l.status} />
                <ReadOnlyField label="Expires" value={l.end_date} />
                <ReadOnlyField label="Machine" value={l.hardware_id} />
              </div>
            ))
          )}
          <Link className="btn btn-secondary" to="/profile">
            Manage licence
          </Link>
        </section>

        <section className="panel">
          <h2>Latest release</h2>
          {latest ? (
            <>
              <ReadOnlyField label="Product" value={latest.name} />
              <ReadOnlyField label="Version" value={latest.version} />
              <ReadOnlyField
                label="Size"
                value={
                  latest.file_size
                    ? `${(latest.file_size / (1024 * 1024)).toFixed(2)} MB`
                    : null
                }
              />
              <Link className="btn btn-primary" to="/products">
                Go to downloads
              </Link>
            </>
          ) : (
            <p className="muted">No products are available yet.</p>
          )}
        </section>

        <section className="panel">
          <h2>Getting started</h2>
          <ul className="quick-links">
            <li>
              <Link to="/help/how-to-use">How to use LuSaaS Trader</Link>
            </li>
            <li>
              <Link to="/help/videos">Video tutorials</Link>
            </li>
            <li>
              <Link to="/help/faq">FAQ</Link>
            </li>
            <li>
              <Link to="/help/contact">Contact support</Link>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}

function Products({ products }) {
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const download = async (id, name) => {
    setError('');
    setBusyId(id);
    try {
      const res = await axios.get(`${API_URL}/download/${id}`, {
        ...authHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${name}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Download failed. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="dashboard">
      <PageHeader title="Your products" description="Download your LuSaaS Trader installers." />
      <Message text={error} />

      {products.length === 0 ? (
        <div className="empty-state">
          <p>No products are available yet.</p>
        </div>
      ) : (
        <div className="products-grid">
          {products.map((p) => (
            <article className="product-card" key={p.id}>
              <div className="product-header">
                <h3>{p.name}</h3>
                {p.version && <span className="version">v{p.version}</span>}
              </div>
              {p.description && <p className="product-description">{p.description}</p>}
              <div className="product-footer">
                <span className="file-size">
                  {p.file_size ? `${(p.file_size / (1024 * 1024)).toFixed(2)} MB` : ''}
                </span>
                <button
                  className="btn btn-primary"
                  onClick={() => download(p.id, p.name)}
                  disabled={busyId === p.id}
                >
                  {busyId === p.id ? 'Preparing...' : 'Download'}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function Profile({ profile, onProfileChange, onUserChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: '', email: '', phone: '', whatsapp: '' });
  const [accountMsg, setAccountMsg] = useState({ text: '', tone: 'error' });
  const [saving, setSaving] = useState(false);

  const [pw, setPw] = useState({ currentPassword: '', newPassword: '' });
  const [pwMsg, setPwMsg] = useState({ text: '', tone: 'error' });

  const [hwDrafts, setHwDrafts] = useState({});
  const [hwMsg, setHwMsg] = useState({ text: '', tone: 'error' });

  const user = profile?.user;
  const licenses = profile?.licenses || [];

  const startEdit = () => {
    setDraft({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      whatsapp: user?.whatsapp || ''
    });
    setAccountMsg({ text: '', tone: 'error' });
    setEditing(true);
  };

  const saveAccount = async (e) => {
    e.preventDefault();
    setSaving(true);
    setAccountMsg({ text: '', tone: 'error' });
    try {
      const { data } = await axios.put(`${API_URL}/user/profile`, draft, authHeader());
      onUserChange(data.user);
      await onProfileChange();
      setEditing(false);
      setAccountMsg({ text: 'Profile saved.', tone: 'success' });
    } catch (err) {
      setAccountMsg({
        text: err.response?.data?.error || 'Could not save your profile.',
        tone: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwMsg({ text: '', tone: 'error' });
    try {
      await axios.put(`${API_URL}/user/password`, pw, authHeader());
      setPw({ currentPassword: '', newPassword: '' });
      setPwMsg({ text: 'Password updated.', tone: 'success' });
    } catch (err) {
      setPwMsg({
        text: err.response?.data?.error || 'Could not update your password.',
        tone: 'error'
      });
    }
  };

  const saveHardware = async (licenseId) => {
    const value = hwDrafts[licenseId];
    setHwMsg({ text: '', tone: 'error' });
    if (!value || !value.trim()) {
      setHwMsg({ text: 'Enter a hardware ID before saving.', tone: 'error' });
      return;
    }
    try {
      await axios.put(
        `${API_URL}/user/hardware-id`,
        { hardware_id: value.trim(), license_id: licenseId },
        authHeader()
      );
      await onProfileChange();
      setHwDrafts((d) => ({ ...d, [licenseId]: undefined }));
      setHwMsg({ text: 'Hardware ID updated.', tone: 'success' });
    } catch (err) {
      setHwMsg({
        text: err.response?.data?.error || 'Could not update the hardware ID.',
        tone: 'error'
      });
    }
  };

  if (!user) {
    return (
      <div className="dashboard">
        <PageHeader title="Profile" />
        <p className="muted">Loading your details...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <PageHeader title="Profile" description="Your account details and licence." />

      <section className="panel">
        <div className="panel-head">
          <h2>Account</h2>
          {!editing && (
            <button className="btn btn-secondary btn-sm" onClick={startEdit}>
              Edit
            </button>
          )}
        </div>

        <Message text={accountMsg.text} tone={accountMsg.tone} />

        {editing ? (
          <form onSubmit={saveAccount}>
            <div className="form-group">
              <label htmlFor="p-name">Full name</label>
              <input
                id="p-name"
                type="text"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="p-email">Email</label>
              <input
                id="p-email"
                type="email"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="p-phone">Phone</label>
              <input
                id="p-phone"
                type="tel"
                value={draft.phone}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="form-group">
              <label htmlFor="p-whatsapp">WhatsApp</label>
              <input
                id="p-whatsapp"
                type="tel"
                value={draft.whatsapp}
                onChange={(e) => setDraft({ ...draft, whatsapp: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="button-row">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save changes'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="field-list">
            <ReadOnlyField label="Full name" value={user.name} />
            <ReadOnlyField label="Email" value={user.email} />
            <ReadOnlyField label="Phone" value={user.phone} />
            <ReadOnlyField label="WhatsApp" value={user.whatsapp} />
          </div>
        )}
      </section>

      <section className="panel">
        <h2>Password</h2>
        <Message text={pwMsg.text} tone={pwMsg.tone} />
        <form onSubmit={savePassword}>
          <div className="form-group">
            <label htmlFor="p-current">Current password</label>
            <input
              id="p-current"
              type="password"
              value={pw.currentPassword}
              onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="p-new">New password</label>
            <input
              id="p-new"
              type="password"
              value={pw.newPassword}
              onChange={(e) => setPw({ ...pw, newPassword: e.target.value })}
              placeholder="At least 6 characters"
              required
            />
          </div>
          <button type="submit" className="btn btn-primary">
            Change password
          </button>
        </form>
      </section>

      <section className="panel">
        <h2>Licence</h2>
        <Message text={hwMsg.text} tone={hwMsg.tone} />

        {licenses.length === 0 ? (
          <p className="muted">No licence is linked to this email yet.</p>
        ) : (
          licenses.map((l, i) => (
            <div className="licence-block" key={l.id}>
              <h3>
                {l.product || 'IBKR'}
                {licenses.length > 1 ? ` \u00B7 seat ${i + 1}` : ''}
              </h3>
              <div className="field-list">
                <ReadOnlyField label="Key" value={l.license_key} />
                <ReadOnlyField label="Type" value={l.license_type} />
                <ReadOnlyField label="Status" value={l.status} />
                <ReadOnlyField label="Expires" value={l.end_date} />
              </div>
              <div className="form-group">
                <label htmlFor={`hw-${l.id}`}>Hardware ID</label>
                <input
                  id={`hw-${l.id}`}
                  type="text"
                  value={
                    hwDrafts[l.id] !== undefined ? hwDrafts[l.id] : l.hardware_id || ''
                  }
                  onChange={(e) =>
                    setHwDrafts((d) => ({ ...d, [l.id]: e.target.value }))
                  }
                  placeholder="Shown in the engine console at startup"
                />
                <p className="field-hint">
                  Change this to move the licence to a different machine.
                </p>
              </div>
              <button className="btn btn-primary" onClick={() => saveHardware(l.id)}>
                Save hardware ID
              </button>
            </div>
          ))
        )}
      </section>
    </div>
  );
}

function Admin({ products, onProductsChange }) {
  const [form, setForm] = useState({ name: '', description: '', version: '1.0', file: null });
  const [msg, setMsg] = useState({ text: '', tone: 'error' });
  const [uploading, setUploading] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    setMsg({ text: '', tone: 'error' });

    if (!form.name || !form.file) {
      setMsg({ text: 'A product name and a .zip file are both required.', tone: 'error' });
      return;
    }

    const body = new FormData();
    body.append('name', form.name);
    body.append('description', form.description);
    body.append('version', form.version);
    body.append('file', form.file);

    setUploading(true);
    try {
      await axios.post(`${API_URL}/admin/products`, body, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setForm({ name: '', description: '', version: '1.0', file: null });
      e.target.reset();
      await onProductsChange();
      setMsg({ text: 'Product uploaded.', tone: 'success' });
    } catch (err) {
      setMsg({ text: err.response?.data?.error || 'Upload failed.', tone: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this product? The installer file is removed too.')) return;
    setMsg({ text: '', tone: 'error' });
    try {
      await axios.delete(`${API_URL}/admin/products/${id}`, authHeader());
      await onProductsChange();
      setMsg({ text: 'Product deleted.', tone: 'success' });
    } catch (err) {
      setMsg({ text: err.response?.data?.error || 'Delete failed.', tone: 'error' });
    }
  };

  return (
    <div className="dashboard">
      <PageHeader title="Product management" description="Upload and manage installers." />
      <Message text={msg.text} tone={msg.tone} />

      <div className="admin-grid">
        <section className="panel">
          <h2>Upload a product</h2>
          <form onSubmit={add}>
            <div className="form-group">
              <label htmlFor="a-name">Product name</label>
              <input
                id="a-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="LuSaaS Trader"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="a-desc">Description</label>
              <textarea
                id="a-desc"
                rows="3"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="a-version">Version</label>
              <input
                id="a-version"
                type="text"
                value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="a-file">Installer (.zip)</label>
              <input
                id="a-file"
                type="file"
                accept=".zip"
                onChange={(e) => setForm({ ...form, file: e.target.files[0] })}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary full-width" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload product'}
            </button>
          </form>
        </section>

        <section className="panel">
          <h2>Current products ({products.length})</h2>
          {products.length === 0 ? (
            <p className="muted">Nothing uploaded yet.</p>
          ) : (
            <div className="products-list">
              {products.map((p) => (
                <div className="product-item" key={p.id}>
                  <div>
                    <strong>{p.name}</strong>
                    <span className="item-meta">
                      v{p.version}
                      {p.file_size
                        ? ` \u00B7 ${(p.file_size / (1024 * 1024)).toFixed(2)} MB`
                        : ''}
                    </span>
                  </div>
                  <button className="btn btn-danger btn-sm" onClick={() => remove(p.id)}>
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function AdminLicenses() {
  const [licenses, setLicenses] = useState([]);
  const [usage, setUsage] = useState({});
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/admin/licenses`, authHeader());
        setLicenses(data);
      } catch (err) {
        setError(err.response?.data?.error || 'Could not load licences.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = async (id) => {
    if (openId === id) {
      setOpenId(null);
      return;
    }
    setOpenId(id);
    if (usage[id]) return;
    try {
      const { data } = await axios.get(`${API_URL}/admin/usage/${id}`, authHeader());
      setUsage((u) => ({ ...u, [id]: data }));
    } catch (err) {
      setUsage((u) => ({ ...u, [id]: [] }));
    }
  };

  const shortTime = (iso) => {
    if (!iso) return '\u2014';
    try {
      return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (err) {
      return iso;
    }
  };

  const daysLeft = (end) => {
    if (!end) return null;
    const diff = Math.ceil((new Date(end) - new Date()) / 86400000);
    return diff;
  };

  if (loading) {
    return (
      <div className="dashboard">
        <PageHeader title="Licences" />
        <p className="muted">Loading...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <PageHeader
        title="Licences and activity"
        description="Every licence seat, and which days the engine actually ran."
      />
      <Message text={error} />

      {licenses.length === 0 ? (
        <div className="empty-state">
          <p>No licences in the database.</p>
        </div>
      ) : (
        <div className="licence-table">
          {licenses.map((l) => {
            const left = daysLeft(l.end_date);
            const rows = usage[l.id];
            return (
              <div className="licence-row" key={l.id}>
                <button className="licence-row-head" onClick={() => toggle(l.id)}>
                  <div className="lr-main">
                    <strong>{l.email}</strong>
                    <span className="lr-meta">
                      {l.product || 'IBKR'} {'\u00B7'} {l.license_key} {'\u00B7'}{' '}
                      {l.license_type || 'TRIAL'}
                    </span>
                  </div>
                  <div className="lr-stats">
                    <span className={`pill ${l.status === 'ACTIVE' ? 'pill-ok' : 'pill-off'}`}>
                      {l.status}
                    </span>
                    <span className="lr-figure">
                      <b>{l.active_days || 0}</b> active {l.active_days === 1 ? 'day' : 'days'}
                    </span>
                    <span className="lr-figure">
                      {left === null
                        ? '\u2014'
                        : left < 0
                        ? 'expired'
                        : `${left}d left`}
                    </span>
                    <span className="lr-caret">{openId === l.id ? '\u2212' : '+'}</span>
                  </div>
                </button>

                {openId === l.id && (
                  <div className="licence-row-body">
                    <div className="field-list">
                      <ReadOnlyField label="Machine" value={l.hardware_id} />
                      <ReadOnlyField label="Starts" value={l.start_date} />
                      <ReadOnlyField label="Ends" value={l.end_date} />
                      <ReadOnlyField label="Last seen" value={l.last_seen} />
                    </div>

                    <h4>Daily activity</h4>
                    {!rows ? (
                      <p className="muted">Loading...</p>
                    ) : rows.length === 0 ? (
                      <p className="muted">
                        Never run. This licence has been issued but the engine has
                        not started with it.
                      </p>
                    ) : (
                      <table className="usage-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>First</th>
                            <th>Last</th>
                            <th>Starts</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.usage_date}>
                              <td>{r.usage_date}</td>
                              <td>{shortTime(r.first_seen)}</td>
                              <td>{shortTime(r.last_seen)}</td>
                              <td>{r.start_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/admin/messages`, authHeader());
      setMessages(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load messages.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = async (m) => {
    if (openId === m.id) {
      setOpenId(null);
      return;
    }
    setOpenId(m.id);
    if (!m.is_read) {
      try {
        await axios.put(
          `${API_URL}/admin/messages/${m.id}/read`,
          { is_read: true },
          authHeader()
        );
        setMessages((list) =>
          list.map((x) => (x.id === m.id ? { ...x, is_read: 1 } : x))
        );
      } catch (err) {
        /* not worth surfacing */
      }
    }
  };

  const when = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso.replace(' ', 'T') + 'Z').toLocaleString();
    } catch (err) {
      return iso;
    }
  };

  const unread = messages.filter((m) => !m.is_read).length;

  if (loading) {
    return (
      <div className="dashboard">
        <PageHeader title="Messages" />
        <p className="muted">Loading...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <PageHeader
        title="Messages"
        description={
          unread > 0
            ? `${unread} unread of ${messages.length}`
            : `${messages.length} message${messages.length === 1 ? '' : 's'}`
        }
      />
      <Message text={error} />

      {messages.length === 0 ? (
        <div className="empty-state">
          <p>Nothing has come through the contact form yet.</p>
        </div>
      ) : (
        <div className="licence-table">
          {messages.map((m) => (
            <div className={`licence-row ${m.is_read ? '' : 'row-unread'}`} key={m.id}>
              <button className="licence-row-head" onClick={() => open(m)}>
                <div className="lr-main">
                  <strong>{m.subject || '(no subject)'}</strong>
                  <span className="lr-meta">
                    {m.name ? `${m.name} \u00B7 ` : ''}
                    {m.email}
                    {m.user_id ? ` \u00B7 account #${m.user_id}` : ''}
                  </span>
                </div>
                <div className="lr-stats">
                  {!m.is_read && <span className="pill pill-new">new</span>}
                  {!m.emailed && <span className="pill pill-off">not emailed</span>}
                  <span className="lr-figure">{when(m.created_at)}</span>
                  <span className="lr-caret">{openId === m.id ? '\u2212' : '+'}</span>
                </div>
              </button>

              {openId === m.id && (
                <div className="licence-row-body">
                  <pre className="message-body">{m.message}</pre>
                  <a className="btn btn-secondary btn-sm" href={`mailto:${m.email}`}>
                    Reply by email
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Route guards and layout                                            */
/* ------------------------------------------------------------------ */

function RequireAuth({ user, ready, children }) {
  const location = useLocation();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Shell({ user, isAdmin, onLogout, children }) {
  const isMobile = () => window.innerWidth <= 768;

  const [sidebarOpen, setSidebarOpen] = useState(
    typeof window !== 'undefined' ? window.innerWidth > 768 && !!user : false
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onResize = () => {
      setSidebarOpen(!isMobile() && !!user);
      if (!isMobile()) setMobileNavOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [user]);

  useEffect(() => {
    setSidebarOpen(!isMobile() && !!user);
    setMobileNavOpen(false);
  }, [user]);

  // Close the visitor menu whenever the route changes.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const closeOnMobile = () => {
    if (isMobile()) setSidebarOpen(false);
  };

  return (
    <div className="app">
      <Header
        user={user}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen((v) => !v)}
        mobileNavOpen={mobileNavOpen}
        onToggleMobileNav={() => setMobileNavOpen((v) => !v)}
        onLogout={onLogout}
      />

      {!user && <MobileNav open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />}

      <div className="container">
        {user && sidebarOpen && (
          <div
            className="sidebar-backdrop"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}
        {user && (
          <Sidebar isAdmin={isAdmin} open={sidebarOpen} onNavigate={closeOnMobile} />
        )}
        <main className="main-content">{children}</main>
      </div>

      <Footer />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  App                                                                */
/* ------------------------------------------------------------------ */

function AppRoutes() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  const loadProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/products`);
      setProducts(data);
    } catch (err) {
      setProducts([]);
    }
  }, []);

  const loadProfile = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const { data } = await axios.get(`${API_URL}/user/profile`, authHeader());
      setProfile(data);
      setIsAdmin(!!data.user?.is_admin);
    } catch (err) {
      if (err.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setProfile(null);
        setIsAdmin(false);
      }
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const stored = localStorage.getItem('user');
    if (token && stored) {
      try {
        setUser(JSON.parse(stored));
      } catch (err) {
        localStorage.removeItem('user');
      }
    }
    Promise.all([loadProducts(), loadProfile()]).finally(() => setReady(true));
  }, [loadProducts, loadProfile]);

  const handleAuth = (data) => {
    const u = { id: data.userId, email: data.email, name: data.name };
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(u));
    setUser(u);
    setIsAdmin(!!data.is_admin);
    loadProfile();
    loadProducts();
  };

  const handleUserChange = (updated) => {
    const merged = { ...user, ...updated };
    setUser(merged);
    localStorage.setItem('user', JSON.stringify(merged));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setProfile(null);
    setIsAdmin(false);
    navigate('/');
  };

  const guard = (element) => (
    <RequireAuth user={user} ready={ready}>
      {element}
    </RequireAuth>
  );

  return (
    <Shell user={user} isAdmin={isAdmin} onLogout={handleLogout}>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={user ? <Navigate to="/home" replace /> : <Landing />} />
        <Route path="/login" element={user ? <Navigate to="/home" replace /> : <Login onAuth={handleAuth} />} />
        <Route
          path="/register"
          element={user ? <Navigate to="/home" replace /> : <Register onAuth={handleAuth} />}
        />

        <Route
          path="/home"
          element={guard(<Home user={user || {}} profile={profile} products={products} />)}
        />
        <Route path="/products" element={guard(<Products products={products} />)} />
        <Route
          path="/profile"
          element={guard(
            <Profile
              profile={profile}
              onProfileChange={loadProfile}
              onUserChange={handleUserChange}
            />
          )}
        />
        <Route
          path="/admin"
          element={guard(
            isAdmin ? (
              <Admin products={products} onProductsChange={loadProducts} />
            ) : (
              <Navigate to="/home" replace />
            )
          )}
        />
        <Route
          path="/admin/licenses"
          element={guard(isAdmin ? <AdminLicenses /> : <Navigate to="/home" replace />)}
        />
        <Route
          path="/admin/messages"
          element={guard(isAdmin ? <AdminMessages /> : <Navigate to="/home" replace />)}
        />

        <Route path="/help" element={<HelpIndex />} />
        <Route path="/help/:slug" element={<HelpTopic contactUser={user} />} />

        <Route path="/about" element={<StaticPage slug="about" />} />
        <Route path="/privacy" element={<StaticPage slug="privacy" />} />
        <Route path="/terms" element={<StaticPage slug="terms" />} />
        <Route path="/disclaimer" element={<StaticPage slug="disclaimer" />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Shell>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
